import type { InlineCardAttributes } from "./cards/types";
import type { LinkNodeAttributes } from "./types";
import { combineTransactionSteps, findChildrenInRange, getChangedRanges } from "@tiptap/core";
import Link from "@tiptap/extension-link";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { withoutProtocol, withProtocol } from "ufo";
import { isLinkMark } from "../utils";

export function linkMarkToLinkTransformer() {
  return new Plugin({
    key: new PluginKey("linkToLinkNodeTransformer"),
    appendTransaction(transactions, oldState, state) {
      const docChanges = transactions.some(transaction => transaction.docChanged) && !oldState.doc.eq(state.doc);
      if (!docChanges) return;

      const inlineCardSpec = state.schema.nodes.inlineCard;
      const linkNodeSpec = state.schema.nodes.linkNode;
      const linkMarkSpec = state.schema.marks[Link.name];
      if (!linkMarkSpec) return;
      if (!linkNodeSpec && !inlineCardSpec) return;

      const { tr } = state;
      const transform = combineTransactionSteps(oldState.doc, [...transactions]);
      const changes = getChangedRanges(transform);
      if (!changes.length) return;

      for (const change of changes) {
        const { newRange } = change;
        const linksInChangedRanges = findChildrenInRange(
          state.doc,
          newRange,
          node => node.marks.some(m => m.type === linkMarkSpec),
        );

        for (const linkInChangedRange of linksInChangedRanges) {
          const { node, pos } = linkInChangedRange;
          const linkMark = node.marks.find(m => m.type === linkMarkSpec);

          if (!linkMark || !isLinkMark(linkMark)) continue;

          let href = linkMark.attrs.href;
          const text = node.textContent;

          const from = tr.mapping.map(pos);
          const to = from + node.nodeSize;
          let newNode;
          const isEmailHref = href.startsWith("mailto:");

          // create inline card if href of link mark matches its text and href isn't email
          const shouldCreateInlineCard = !isEmailHref && (href === text || withoutProtocol(href) === text);
          if (shouldCreateInlineCard && inlineCardSpec) {
            // by default link marks are created with 'http' protocol
            // in cases when text is without protocol at all, force 'https'
            if (!text.startsWith("http://")) {
              href = withProtocol(href, "https://");
            }
            const attrs: InlineCardAttributes = {
              url: href,
              text: null,
            };
            newNode = inlineCardSpec.create(attrs);
          } else if (linkNodeSpec) {
            const attrs: LinkNodeAttributes = {
              url: href,
              text,
            };
            newNode = linkNodeSpec.create(attrs);
          }

          if (newNode) {
            tr.replaceRangeWith(from, to, newNode);
          }
        }
      }

      if (!tr.steps.length) {
        return;
      }

      return tr;
    },
  });
}
